/**
 * Typed API client for the Braim backend.
 *
 * Every request carries the Supabase access token. The backend re-verifies it on
 * each call — the frontend is never the authority on identity, it only presents
 * a credential.
 *
 * Errors are normalised into `ApiError`, which preserves the backend's stable
 * `code` field. That matters most for the shared-data conflict flow, where
 * `record_deleted` and `record_modified` both arrive as HTTP 409 but require
 * completely different UI responses.
 */
import { getAccessToken } from './supabase';
import { ENV } from './env';

const BASE_URL = ENV.apiBaseUrl;

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Someone else deleted the record we were editing. */
  get isDeletedConflict(): boolean {
    return this.status === 409 && this.code === 'record_deleted';
  }

  /** Someone else changed the record we were editing. */
  get isModifiedConflict(): boolean {
    return this.status === 409 && this.code === 'record_modified';
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Aborts the request, used to cancel in-flight polls on unmount. */
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const token = await getAccessToken();
  const method = options.method ?? 'GET';

  let res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  });

  // One quiet retry for rate-limited GETs: the app polls several endpoints on
  // timers, and a burst collision (several tabs, an export) should degrade to
  // "slightly later" — not paint error banners over every open view. GET only:
  // retrying a mutation could apply it twice.
  if (res.status === 429 && method === 'GET') {
    const retryAfter = Number(res.headers.get('Retry-After'));
    const waitMs = Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter * 1000 : 2_000;
    await new Promise((resolve) => setTimeout(resolve, Math.min(waitMs, 10_000)));
    res = await fetch(`${BASE_URL}${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      signal: options.signal,
    });
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let payload: unknown;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    // Non-JSON body (a proxy error page, a truncated response). On an error
    // status the body text is the best message we have; on a 200 the caller
    // expected JSON and silently returning null would crash far from here.
    throw new ApiError(res.status, 'invalid_response', text.slice(0, 300));
  }

  if (!res.ok) {
    const err = (payload as { error?: { code?: string; message?: string; details?: unknown } })
      ?.error;
    throw new ApiError(
      res.status,
      err?.code ?? 'unknown_error',
      err?.message ?? `Request failed with ${res.status}`,
      err?.details,
    );
  }

  return payload as T;
}

/**
 * Downloads a streamed file (the whole-table exports). Reads the body
 * incrementally so a 100 MB export never exists as one JS string, and reports
 * received bytes so the UI can show live progress. `X-Total-Rows` (exposed via
 * CORS) carries the row count without the client parsing what it downloaded.
 */
async function download(
  path: string,
  onProgress?: (bytes: number) => void,
): Promise<{ blob: Blob; totalRows: number | null }> {
  const token = await getAccessToken();
  const res = await fetch(`${BASE_URL}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!res.ok) {
    let payload: unknown = null;
    try {
      payload = JSON.parse(await res.text());
    } catch {
      /* non-JSON error body — fall through to the generic message */
    }
    const err = (payload as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiError(
      res.status,
      err?.code ?? 'unknown_error',
      err?.message ?? `Export failed with ${res.status}`,
    );
  }

  const header = res.headers.get('X-Total-Rows');
  const totalRows = header !== null && header !== '' ? Number(header) : null;
  const contentType = res.headers.get('Content-Type') ?? 'application/octet-stream';

  if (!res.body) {
    return { blob: await res.blob(), totalRows };
  }
  const reader = res.body.getReader();
  const chunks: BlobPart[] = [];
  let received = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value as Uint8Array<ArrayBuffer>);
    received += value.byteLength;
    onProgress?.(received);
  }
  return { blob: new Blob(chunks, { type: contentType }), totalRows };
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string, body?: unknown) => request<T>(path, { method: 'DELETE', body }),
  download,
};

// --- Shapes returned by the backend ----------------------------------------

export interface EegRecordSummary {
  id: number;
  user_id: number;
  record_id_v2: string | null;
  record_type: string | null;
  n_channels: number | null;
  sample_rate: number | null;
  electrodes: string[] | null;
  n_packets: number | null;
  packet_length: number | null;
  duration_seconds: number | null;
  device: string | null;
  created_at: string;
}

// --- NeuroCloud (derived analytics; see backend/src/routes/neurocloud.ts) ----

export interface NcOverview {
  counts: {
    computedValues: number;
    windowed: number;
    preprocessed: number;
    groupStats: number;
    markers: number;
  };
  distinctUsers: number;
  preprocessedCoverage: { distinctIngests: number; inLoadedBatch: number };
  versions: { version: string; count: number }[];
}

/** One scalar result. `data` is numeric(10,5) — arrives as a string from pg. */
export interface NcMarker {
  id: number;
  user_id: number;
  /** Per-user ordinal (0, 1, 2…), NOT a sessions.id. */
  session_id: number;
  name: string;
  data: string;
}

export interface NcMarkerName {
  name: string;
  count: number;
}

export interface NcSessionInfo {
  sessionId: number;
  series: number;
}

/** One entry of the time-series user picker. */
export interface NcSeriesUser {
  userId: number;
  sessions: number;
}

export interface NcSeries {
  id: number;
  name: string;
  data: number[];
  created_at: string;
}

export interface NcGroupStat {
  id: number;
  group_id: number;
  name: string;
  data: Record<string, unknown>;
  created_at: string;
}

export interface NcPreprocessed {
  id: number;
  ingest_id: number;
  name: string;
  user_id: number;
  /** Per-user ordinal, NOT a sessions.id. */
  session_id: number;
  element_idx: number;
  /** When NeuroCloud processed it — NOT when the EEG was recorded. */
  created_at: string;
  in_loaded_batch: boolean;
  record_type: string | null;
  n_channels: number | null;
  sample_rate: number | null;
  electrodes: string[] | null;
  duration_seconds: number | null;
  device: string | null;
  /** The raw recording's own timestamp (null when not in the loaded batch). */
  record_created_at: string | null;
}

export interface NcPreprocessedDetail extends Omit<
  NcPreprocessed,
  'electrodes' | 'record_created_at'
> {
  updated_at: string;
  header: Record<string, unknown> | null;
  raw_electrodes: string[] | null;
}

export interface Job {
  id: string;
  status: 'pending' | 'running' | 'done' | 'failed' | 'cancelled';
  total_items: number;
  processed_items: number;
  failed_items: number;
  progress?: number;
  error: string | null;
  params: Record<string, unknown>;
  /** Which data batch (0-based sampler phase) was loaded when this run started. */
  data_phase?: number | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

// --- Data-batch swap (super admin) ------------------------------------------

export type SwapStatus = 'idle' | 'draining' | 'downloading' | 'wiping' | 'loading' | 'failed';

/** Where swaps read batches from: the MEGA S4 bucket or a local dump file. */
export type BatchSource = 's4' | 'dump';

/** Polled by every signed-in client to lock the UI during a swap. */
export interface AppStateResponse {
  swap: {
    active: boolean;
    status: SwapStatus;
    progressPct: number | null;
  };
}

export interface AdminSwapState {
  status: SwapStatus;
  currentPhase: number;
  targetPhase: number | null;
  phasesLoaded: number[];
  progress: { scanned: number; kept: number; bytes: number; pct: number | null };
  error: string | null;
  startedBy: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  heartbeatAt: string | null;
}

export interface AdminBatchesResponse {
  state: AdminSwapState;
  totalPhases: number;
  totalDumpRows: number;
  /** Smallest never-loaded phase, or null when all have been explored. */
  nextPhase: number | null;
  rowsLoaded: number;
  jobs: { running: number; pending: number };
  /** Source the next swap would use; null when neither S4 nor a dump exists. */
  source: BatchSource | null;
  dump: { available: boolean; path: string };
  s4: { endpoint: string; bucket: string; prefix: string } | null;
  strata: { channels: string; stride: number }[];
}

export interface JobResult {
  id: string;
  job_id: string;
  ingest_id: number;
  record_id_v2: string | null;
  n_channels: number | null;
  n_windows: number | null;
  window_samples: number | null;
  electrodes: string[] | null;
  source_sfreq: number | null;
  embedding_dim: number | null;
  embedding?: number[];
  metrics: Record<string, unknown>;
  status: 'ok' | 'skipped' | 'failed';
  error: string | null;
  created_at: string;
}

export interface Note {
  id: string;
  title: string;
  body: string;
  created_at: string;
  updated_at: string;
}

export interface SharedTableMeta {
  name: string;
  label: string;
  pk: string;
  readable: string[];
  editable: string[];
  searchable: string[];
  /** Categorical column driving the filter chips; null → free-text search. */
  facetColumn: string | null;
}

export interface SharedListResponse {
  table: string;
  label: string;
  rows: Record<string, unknown>[];
  total: number;
  limit: number;
  offset: number;
  editable: string[];
  pk: string;
  fetchedAt: string;
}
